import { useState, useCallback, useMemo } from 'react';
import type {
  VisualizationMode,
  VisualizationConfig,
  VisualizationModeInfo,
} from '@/types/visualization';
import {
  VISUALIZATION_MODES,
  DEFAULT_VISUALIZATION_CONFIG,
} from '@/types/visualization';

export interface UseVisualizationModeReturn {
  config: VisualizationConfig;
  setMode: (mode: VisualizationMode) => void;
  setIntensity: (intensity: number) => void;
  toggleOption: (option: 'showLabels' | 'showScores' | 'applyToRegions') => void;
  resetToDefaults: () => void;
  isActive: (mode: VisualizationMode) => boolean;
  getModeInfo: (mode: VisualizationMode) => VisualizationModeInfo;
  availableModes: VisualizationModeInfo[];
  currentModeInfo: VisualizationModeInfo;
}

export function useVisualizationMode(
  initialConfig?: Partial<VisualizationConfig>
): UseVisualizationModeReturn {
  const [config, setConfig] = useState<VisualizationConfig>({
    ...DEFAULT_VISUALIZATION_CONFIG,
    ...initialConfig,
  });

  const setMode = useCallback((mode: VisualizationMode) => {
    setConfig(prev => ({
      ...prev,
      mode,
      intensity: VISUALIZATION_MODES[mode].defaultIntensity,
    }));
  }, []);

  const setIntensity = useCallback((intensity: number) => {
    setConfig(prev => ({ ...prev, intensity: Math.max(0, Math.min(100, intensity)) }));
  }, []);

  const toggleOption = useCallback(
    (option: 'showLabels' | 'showScores' | 'applyToRegions') => {
      setConfig(prev => ({ ...prev, [option]: !prev[option] }));
    },
    []
  );

  const resetToDefaults = useCallback(() => {
    setConfig(DEFAULT_VISUALIZATION_CONFIG);
  }, []);

  const isActive = useCallback(
    (mode: VisualizationMode) => config.mode === mode,
    [config.mode]
  );

  const getModeInfo = useCallback(
    (mode: VisualizationMode) => VISUALIZATION_MODES[mode],
    []
  );

  const availableModes = useMemo(
    () => Object.values(VISUALIZATION_MODES),
    []
  );

  const currentModeInfo = useMemo(
    () => VISUALIZATION_MODES[config.mode],
    [config.mode]
  );

  return {
    config,
    setMode,
    setIntensity,
    toggleOption,
    resetToDefaults,
    isActive,
    getModeInfo,
    availableModes,
    currentModeInfo,
  };
}

export default useVisualizationMode;
